import {
  AUTH_HO_QUALITY_ENGINEERING,
  AUTH_HUB_SPV,
  AUTH_PRODUCT_MANAGER,
  AUTH_SHIFT_LEAD,
  AUTH_SPV_HUB,
  type RolesKeyType,
} from 'constant/roles'
import type { AuthInitialStateType } from 'features/Auth/redux/Auth/slice'

type UserDataType = AuthInitialStateType['userData']
type LocationSourceType = {
  location_id: number
  location_name: string
  location_type: string
}
type UserLocationType = LocationSourceType & { name: string }

export const PBV1_BYPASS_ROLES: RolesKeyType[] = [AUTH_SHIFT_LEAD]
export const PBV1_LOCATION_ROLES: RolesKeyType[] = [
  AUTH_PRODUCT_MANAGER,
  AUTH_HUB_SPV,
  AUTH_SPV_HUB,
  AUTH_HO_QUALITY_ENGINEERING,
]

export const getPBV2RedirectPath = (status: string) =>
  `/dashboard/ongoing-orders/${status === 'upcoming-order' ? 'upcoming' : status}`

export const formatNonWarehouseLocations = (
  locations: LocationSourceType[] = [],
): UserLocationType[] =>
  locations
    .filter((location) => location.location_type?.toLowerCase() !== 'warehouse')
    .map((location) => ({ ...location, name: location.location_name }))

export const getNonWarehouseUserLocations = (userData: UserDataType): UserLocationType[] =>
  formatNonWarehouseLocations(userData.user_locations)

const normalizeRoleName = (role: string) => role.replace(/^AUTH_/, '')

const hasAllowedPBV1LocationRole = (roleNames: string[] = []) => {
  const normalizedAllowedRoles = new Set(PBV1_LOCATION_ROLES.map(normalizeRoleName))
  return roleNames.some((role) => normalizedAllowedRoles.has(normalizeRoleName(role)))
}

export const getNonWarehouseRoleLocations = (userData: UserDataType): UserLocationType[] =>
  formatNonWarehouseLocations(userData.location_roles)

export const getInitialOngoingLocation = (
  userData: UserDataType,
  requestedLocationId?: string | null,
) => {
  const locations = getNonWarehouseUserLocations(userData)
  const requestedLocation = requestedLocationId
    ? locations.find((location) => String(location.location_id) === String(requestedLocationId)) ??
      null
    : null

  if (requestedLocationId || userData.isSuperAdmin) return requestedLocation

  return locations[0] ?? null
}

export const getInitialOngoingRoleLocation = (
  userData: UserDataType,
  requestedLocationId?: string | null,
) => {
  const locations = getNonWarehouseRoleLocations(userData)
  const requestedLocation = requestedLocationId
    ? locations.find((location) => String(location.location_id) === String(requestedLocationId)) ??
      null
    : null

  if (requestedLocationId || userData.isSuperAdmin) return requestedLocation

  return locations[0] ?? null
}

export const canBypassPBV1Block = (
  userData: UserDataType,
  selectedLocationId?: string | number | null,
) => {
  const authorityNames = new Set((userData.authorities ?? []).map(({ name }) => name))
  if (PBV1_BYPASS_ROLES.some((role) => authorityNames.has(role))) return true

  const selectedLocationRoles = userData.location_roles.find(
    ({ location_id }) => String(location_id) === String(selectedLocationId),
  )?.role_names

  return hasAllowedPBV1LocationRole(selectedLocationRoles)
}
